'use client';

import { useEffect, useState } from 'react';
import { isSameDay } from 'date-fns';
import ReactDatePicker from 'react-datepicker';

import { TextField } from '@petsmart-ui/sparky';

import 'react-datepicker/dist/react-datepicker.css';
import './dateTimePicker.scss';
import { getPhoenixDate } from '../../utils/getPhoenixDateTime';

type OptionalMoment = Date | null;

type DateTimePickerProps = {
    className?: string;
    label?: string;
    fieldnote?: string;
    selectedDateValue?: OptionalMoment;
    onDateChange?: (startDate?: OptionalMoment) => void;
    onBlur?: () => void;
    minDate?: OptionalMoment;
    maxDate?: OptionalMoment;
    minTime?: OptionalMoment;
    maxTime?: OptionalMoment;
    adjustMinTime?: number;
    isError?: boolean;
    disabled?: boolean;
    dateFormat?: string;
    timeFormat?: string;
};

export default function DateTimePicker({
    className = '',
    label = '',
    fieldnote = '',
    selectedDateValue = null,
    onDateChange,
    minDate = new Date(),
    maxDate,
    minTime,
    maxTime,
    onBlur,
    adjustMinTime,
    isError = false,
    disabled = false,
    dateFormat = 'MM/dd/yyyy h:mm a',
    timeFormat = 'h:mm a',
}: Readonly<DateTimePickerProps>) {
    const [selectedDateTime, setSelectedDateTime] = useState<OptionalMoment>(selectedDateValue);

    const arizonaDateTimeZone = Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Phoenix',
    }).resolvedOptions().timeZone;

    const now = getPhoenixDate();

    const resolvedMinTime = (() => {
        if (!minTime && !selectedDateValue) {
            const roundedNow = new Date(now);
            const minutes = now.getMinutes();
            const roundedMinutes = minutes < 30 ? 30 : 0;
            roundedNow.setMinutes(roundedMinutes);
            roundedNow.setSeconds(0);
            roundedNow.setMilliseconds(0);
            if (minutes >= 30) {
                roundedNow.setHours(now.getHours() + 1);
            }
            return roundedNow;
        }

        if (
            (adjustMinTime && minTime && selectedDateValue && minDate && isSameDay(selectedDateValue, minDate)) ||
            (!selectedDateTime && adjustMinTime && minTime)
        ) {
            return new Date(minTime.getTime() + adjustMinTime * 60 * 1000);
        }

        if (
            (minTime && selectedDateValue && minDate && isSameDay(selectedDateValue, minDate)) ||
            (!selectedDateTime && minTime)
        ) {
            return minTime;
        }

        if (selectedDateValue && isSameDay(selectedDateValue, now)) {
            return now;
        }

        return new Date(new Date().setHours(0, 0, 0, 0));
    })();

    const resolvedMaxTime = maxTime ?? new Date(new Date().setHours(23, 59, 59, 999));

    // needed for formik clear form use
    useEffect(() => setSelectedDateTime(selectedDateValue), [selectedDateValue]);

    const handleDateChange = (date: Date | null) => {
        if (!date) {
            setSelectedDateTime(null);
            onDateChange?.(null);
            return;
        }

        const now = new Date();
        const adjustedDate = new Date(date);
        const prevDate = selectedDateTime;

        const isTimeOnly = (d: Date) =>
            d.getFullYear() === now.getFullYear() &&
            d.getMonth() === now.getMonth() &&
            d.getDate() === now.getDate() &&
            (d.getHours() !== 0 || d.getMinutes() !== 0);

        const noDatePreviouslySelected = !prevDate;

        const applyMinDateIfTimeOnly = () => {
            if (noDatePreviouslySelected && isTimeOnly(date) && minDate) {
                adjustedDate.setFullYear(minDate.getFullYear());
                adjustedDate.setMonth(minDate.getMonth());
                adjustedDate.setDate(minDate.getDate());
                setSelectedDateTime(adjustedDate);
                onDateChange?.(adjustedDate);
                return true;
            }
            return false;
        };

        if (applyMinDateIfTimeOnly()) return;

        const isSameDate = (d1: Date | null, d2: Date) => (d1 ? isSameDay(d1, d2) : false);

        const isToday = isSameDay(date, now);
        const isMinDate = minDate && isSameDay(date, minDate);
        const sameDateSelected = isSameDate(prevDate, date);

        const isTimeChanged =
            !prevDate || prevDate.getHours() !== date.getHours() || prevDate.getMinutes() !== date.getMinutes();

        const manuallySelectedTime = sameDateSelected && isTimeChanged;

        const getBaseTime = (): Date => {
            if (minTime) {
                let base = new Date(minTime);
                if (adjustMinTime) {
                    base = new Date(base.getTime() + adjustMinTime * 60 * 1000);
                }
                return base;
            }

            const base = new Date(now);
            const minutes = now.getMinutes();
            const roundedMinutes = minutes < 30 ? 30 : 0;
            base.setMinutes(roundedMinutes);
            base.setSeconds(0);
            base.setMilliseconds(0);
            if (minutes >= 30) {
                base.setHours(now.getHours() + 1);
            }
            return base;
        };

        if ((isToday || isMinDate) && !manuallySelectedTime) {
            const baseTime = getBaseTime();
            adjustedDate.setHours(baseTime.getHours());
            adjustedDate.setMinutes(baseTime.getMinutes());
            adjustedDate.setSeconds(0);
            adjustedDate.setMilliseconds(0);
        } else if (!manuallySelectedTime) {
            adjustedDate.setHours(0, 0, 0, 0);
        }

        setSelectedDateTime(adjustedDate);
        onDateChange?.(adjustedDate);
    };

    return (
        <ReactDatePicker
            className={className}
            selected={selectedDateTime ?? null}
            onChange={handleDateChange}
            showTimeSelect
            timeFormat={timeFormat}
            timeCaption="Time"
            dateFormat={dateFormat}
            disabled={disabled}
            minDate={minDate ?? undefined}
            maxDate={maxDate ?? undefined}
            onBlur={onBlur}
            minTime={resolvedMinTime}
            maxTime={resolvedMaxTime}
            customInput={
                <TextField label={`${label} (TZ: ${arizonaDateTimeZone})`} fieldNote={fieldnote} isError={isError} />
            }
        />
    );
}
